'use client';

import Script from 'next/script';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { initializeTelemetry } from '@/lib/telemetry';
import type { BetterStackWindow } from '@/lib/telemetry/types';

const AD_BLOCKER_CHECK_DELAY_MS = 5000;

/**
 * Client component that injects the Better Stack JS tag and detects
 * ad-blocker interference. Replaces DatadogInitializer.
 *
 * Renders only the `<Script>` tag (no visible DOM).
 */
export function TelemetryInitializer(): JSX.Element | null {
  const scriptContent: string | null = initializeTelemetry();

  useEffect(() => {
    if (scriptContent === null) return;

    const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
      const bs = (window as BetterStackWindow).betterstack;
      if (!bs) {
        // Better Stack SDK was likely blocked by an ad-blocker.
        // Fire a beacon so the backend can track the coverage gap.
        navigator.sendBeacon('/api/telemetry-beacon', '{}');
      }
    }, AD_BLOCKER_CHECK_DELAY_MS);

    return (): void => {
      clearTimeout(timer);
    };
  }, [scriptContent]);

  if (scriptContent === null) return null;

  return (
    <Script
      id="betterstack-js-tag"
      strategy="afterInteractive"
      // REASON: Better Stack JS tag requires inline script injection — the snippet
      // initializes the betterstack() global before the external b.js loads. This is
      // the vendor-prescribed installation method. The script content is a static
      // string from initializeTelemetry() containing only the token env var, not user input.
      // biome-ignore lint/security/noDangerouslySetInnerHtml: REASON: vendor-required inline script tag installation
      dangerouslySetInnerHTML={{ __html: scriptContent }}
    />
  );
}
